import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  BadgeDollarSign,
  BarChart3,
  CircleDollarSign,
  Clock3,
  Gift,
  LogOut,
  Mail,
  MessageCircle,
  RefreshCw,
  Search,
  ShieldCheck,
  TicketPercent,
  UserRoundCheck,
  UsersRound,
  X,
} from "lucide-react";

import {
  adminLogin,
  adminRequest,
  clearAdminSession,
  getAdminToken,
  getStoredAdminUser,
  saveAdminSession,
} from "../../services/adminServices";

import "../../sass/pages/admin.scss";

const TABS = [
  {
    id: "dashboard",
    label: "Visão geral",
    icon: BarChart3,
  },
  {
    id: "campanhas",
    label: "Campanhas",
    icon: TicketPercent,
  },
  {
    id: "parceiros",
    label: "Parceiros",
    icon: UsersRound,
  },
  {
    id: "conversoes",
    label: "Conversões",
    icon: UserRoundCheck,
  },
  {
    id: "comissoes",
    label: "Comissões",
    icon: BadgeDollarSign,
  },
  {
    id: "followups",
    label: "Follow-up",
    icon: MessageCircle,
  },
  {
    id: "usuarios",
    label: "Usuários",
    icon: ShieldCheck,
  },
];

function formatBRLFromCents(
  cents
) {
  return new Intl.NumberFormat(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL",
    }
  ).format(
    Number(cents || 0) /
      100
  );
}

function formatBRL(
  value
) {
  return new Intl.NumberFormat(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL",
    }
  ).format(
    Number(value || 0)
  );
}

function formatDate(
  value
) {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

function StatusPill({
  children,
  tone = "slate",
}) {
  return (
    <span
      className={`adm-status adm-status--${tone}`}
    >
      {children}
    </span>
  );
}

function getStatusTone(
  value
) {
  const status =
    String(
      value || ""
    )
      .trim()
      .toUpperCase();

  if (
    [
      "PAGO",
      "PAGA",
      "ACTIVE",
      "ATIVO",
      "CONFIRMADO",
    ].includes(
      status
    )
  ) {
    return "green";
  }

  if (
    [
      "TRIAL",
      "TRIALING",
      "PENDENTE",
      "LIBERADA",
      "PAST_DUE",
    ].includes(
      status
    )
  ) {
    return "amber";
  }

  if (
    [
      "CANCELADO",
      "CANCELED",
      "CANCELADA",
      "UNPAID",
    ].includes(
      status
    )
  ) {
    return "red";
  }

  return "slate";
}

function AdminLogin({
  onSuccess,
}) {
  const [
    email,
    setEmail,
  ] = useState("");

  const [
    senha,
    setSenha,
  ] = useState("");

  const [
    remember,
    setRemember,
  ] = useState(false);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const submit =
    async (event) => {
      event.preventDefault();
      setError("");
      setLoading(true);

      try {
        const result =
          await adminLogin({
            email,
            senha,
          });

        saveAdminSession({
          accessToken:
            result.accessToken,
          user:
            result.user,
          remember,
        });

        onSuccess(
          result.user
        );
      } catch (requestError) {
        setError(
          requestError.message ||
          "Não foi possível acessar."
        );
      } finally {
        setLoading(false);
      }
    };

  return (
    <main className="adm-login">
      <section className="adm-login__card">
        <div className="adm-login__badge">
          <ShieldCheck size={18} />
          Área interna
        </div>

        <h1>
          PsyAssist Pro
        </h1>

        <p>
          Administração da plataforma
        </p>

        <form
          onSubmit={submit}
        >
          <label>
            <span>
              E-mail
            </span>

            <input
              type="email"
              value={email}
              onChange={(
                event
              ) =>
                setEmail(
                  event.target
                    .value
                )
              }
              autoComplete="username"
              required
            />
          </label>

          <label>
            <span>
              Senha
            </span>

            <input
              type="password"
              value={senha}
              onChange={(
                event
              ) =>
                setSenha(
                  event.target
                    .value
                )
              }
              autoComplete="current-password"
              required
            />
          </label>

          <label className="adm-login__remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(
                event
              ) =>
                setRemember(
                  event.target
                    .checked
                )
              }
            />

            <span>
              Manter acesso neste dispositivo
            </span>
          </label>

          {error ? (
            <div className="adm-alert adm-alert--error">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={loading}
          >
            {loading
              ? "Validando..."
              : "Acessar painel"}
          </button>
        </form>
      </section>
    </main>
  );
}

function DashboardTab({
  data,
}) {
  const kpis =
    data?.kpis || {};

  const cards = [
    {
      label:
        "Usuários",
      value:
        kpis.totalUsuarios ||
        0,
      hint:
        `${kpis.usuariosAtivos || 0} ativos`,
      icon:
        UsersRound,
    },
    {
      label:
        "Trials ativos",
      value:
        kpis.trialsAtivos ||
        0,
      hint:
        "período de teste",
      icon:
        Gift,
    },
    {
      label:
        "Conversões pagas",
      value:
        kpis.conversoesPagas ||
        0,
      hint:
        `${kpis.conversoesPagasMes || 0} neste mês`,
      icon:
        UserRoundCheck,
    },
    {
      label:
        "MRR estimado",
      value:
        formatBRL(
          kpis.mrrEstimado
        ),
      hint:
        "assinaturas fora de trial",
      icon:
        CircleDollarSign,
    },
    {
      label:
        "Parceiros ativos",
      value:
        kpis.parceirosAtivos ||
        0,
      hint:
        `${kpis.campanhasAtivas || 0} campanhas ativas`,
      icon:
        TicketPercent,
    },
    {
      label:
        "Comissão a pagar",
      value:
        formatBRLFromCents(
          kpis.comissaoPendenteCentavos
        ),
      hint:
        `${formatBRLFromCents(
          kpis.comissaoPagaCentavos
        )} já pagos`,
      icon:
        BadgeDollarSign,
    },
  ];

  return (
    <>
      <section className="adm-kpis">
        {cards.map(
          (card) => {
            const Icon =
              card.icon;

            return (
              <article
                key={
                  card.label
                }
                className="adm-kpi"
              >
                <div className="adm-kpi__icon">
                  <Icon size={20} />
                </div>

                <span>
                  {card.label}
                </span>

                <strong>
                  {card.value}
                </strong>

                <small>
                  {card.hint}
                </small>
              </article>
            );
          }
        )}
      </section>

      <section className="adm-card">
        <header className="adm-card__head">
          <div>
            <span>
              Aquisição
            </span>

            <h2>
              Resgates recentes
            </h2>
          </div>
        </header>

        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>
                  Código
                </th>
                <th>
                  E-mail
                </th>
                <th>
                  Plano
                </th>
                <th>
                  Status
                </th>
                <th>
                  Conversão
                </th>
                <th>
                  Data
                </th>
              </tr>
            </thead>

            <tbody>
              {(data
                ?.resgatesRecentes ||
                []
              ).map(
                (item) => (
                  <tr
                    key={
                      item._id
                    }
                  >
                    <td>
                      <strong>
                        {
                          item.codigo
                        }
                      </strong>
                    </td>

                    <td>
                      {item.email ||
                        item
                          .usuarioId
                          ?.email ||
                        "—"}
                    </td>

                    <td>
                      {item.planId ||
                        "—"}
                    </td>

                    <td>
                      <StatusPill
                        tone={getStatusTone(
                          item.status
                        )}
                      >
                        {item.status}
                      </StatusPill>
                    </td>

                    <td>
                      <StatusPill
                        tone={getStatusTone(
                          item.conversaoStatus
                        )}
                      >
                        {item.conversaoStatus}
                      </StatusPill>
                    </td>

                    <td>
                      {formatDate(
                        item.createdAt
                      )}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function CampaignsTab({
  campanhas,
  parceiros,
  onRefresh,
}) {
  const initial = {
    codigo: "",
    nome: "",
    tipo: "CAMPANHA",
    parceiroId: "",
    stripeCouponId: "",
    descontoPercentual:
      20,
    duracaoMeses:
      3,
    limiteUsos:
      100,
    planosPermitidos: [
      "essencial",
      "premium",
    ],
    descricaoPublica:
      "",
  };

  const [
    form,
    setForm,
  ] = useState(
    initial
  );

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    message,
    setMessage,
  ] = useState("");

  const update =
    (
      field,
      value
    ) => {
      setForm(
        (current) => ({
          ...current,
          [field]:
            value,
        })
      );
    };

  const submit =
    async (event) => {
      event.preventDefault();
      setSaving(true);
      setMessage("");

      try {
        await adminRequest(
          "/psy/admin/campanhas",
          {
            method:
              "POST",
            body:
              JSON.stringify({
                ...form,
                parceiroId:
                  form.tipo ===
                  "PARCEIRO"
                    ? form.parceiroId
                    : null,
              }),
          }
        );

        setForm(
          initial
        );

        setMessage(
          "Campanha criada."
        );

        await onRefresh();
      } catch (error) {
        setMessage(
          error.message
        );
      } finally {
        setSaving(false);
      }
    };

  const toggle =
    async (item) => {
      await adminRequest(
        `/psy/admin/campanhas/${item._id}`,
        {
          method:
            "PUT",
          body:
            JSON.stringify({
              ativo:
                !item.ativo,
            }),
        }
      );

      await onRefresh();
    };

  return (
    <div className="adm-two-column">
      <section className="adm-card">
        <header className="adm-card__head">
          <div>
            <span>
              Nova campanha
            </span>

            <h2>
              Cupom e atribuição
            </h2>
          </div>
        </header>

        <form
          className="adm-form"
          onSubmit={submit}
        >
          <div className="adm-form__grid">
            <label>
              <span>
                Código
              </span>

              <input
                value={
                  form.codigo
                }
                onChange={(
                  event
                ) =>
                  update(
                    "codigo",
                    event.target
                      .value
                      .toUpperCase()
                  )
                }
                placeholder="ANA20"
                required
              />
            </label>

            <label>
              <span>
                Nome
              </span>

              <input
                value={
                  form.nome
                }
                onChange={(
                  event
                ) =>
                  update(
                    "nome",
                    event.target
                      .value
                  )
                }
                placeholder="Campanha Ana"
                required
              />
            </label>

            <label>
              <span>
                Tipo
              </span>

              <select
                value={
                  form.tipo
                }
                onChange={(
                  event
                ) =>
                  update(
                    "tipo",
                    event.target
                      .value
                  )
                }
              >
                <option value="CAMPANHA">
                  Campanha
                </option>
                <option value="PARCEIRO">
                  Parceiro
                </option>
              </select>
            </label>

            {form.tipo ===
            "PARCEIRO" ? (
              <label>
                <span>
                  Parceiro
                </span>

                <select
                  value={
                    form.parceiroId
                  }
                  onChange={(
                    event
                  ) =>
                    update(
                      "parceiroId",
                      event.target
                        .value
                    )
                  }
                  required
                >
                  <option value="">
                    Selecione
                  </option>

                  {parceiros.map(
                    (
                      parceiro
                    ) => (
                      <option
                        key={
                          parceiro._id
                        }
                        value={
                          parceiro._id
                        }
                      >
                        {
                          parceiro.nome
                        }
                      </option>
                    )
                  )}
                </select>
              </label>
            ) : null}

            <label>
              <span>
                Stripe Coupon ID
              </span>

              <input
                value={
                  form.stripeCouponId
                }
                onChange={(
                  event
                ) =>
                  update(
                    "stripeCouponId",
                    event.target
                      .value
                  )
                }
                placeholder="ID do Coupon no Stripe"
                required
              />
            </label>

            <label>
              <span>
                Desconto %
              </span>

              <input
                type="number"
                min="0"
                max="100"
                value={
                  form.descontoPercentual
                }
                onChange={(
                  event
                ) =>
                  update(
                    "descontoPercentual",
                    Number(
                      event.target
                        .value
                    )
                  )
                }
                required
              />
            </label>

            <label>
              <span>
                Duração (meses)
              </span>

              <input
                type="number"
                min="1"
                value={
                  form.duracaoMeses
                }
                onChange={(
                  event
                ) =>
                  update(
                    "duracaoMeses",
                    Number(
                      event.target
                        .value
                    )
                  )
                }
                required
              />
            </label>

            <label>
              <span>
                Limite de usos
              </span>

              <input
                type="number"
                min="1"
                value={
                  form.limiteUsos
                }
                onChange={(
                  event
                ) =>
                  update(
                    "limiteUsos",
                    Number(
                      event.target
                        .value
                    )
                  )
                }
                required
              />
            </label>
          </div>

          <label>
            <span>
              Descrição pública
            </span>

            <textarea
              value={
                form.descricaoPublica
              }
              onChange={(
                event
              ) =>
                update(
                  "descricaoPublica",
                  event.target
                    .value
                )
              }
              rows={3}
            />
          </label>

          {message ? (
            <div className="adm-alert">
              {message}
            </div>
          ) : null}

          <button
            className="adm-primary"
            type="submit"
            disabled={saving}
          >
            {saving
              ? "Salvando..."
              : "Criar campanha"}
          </button>
        </form>
      </section>

      <section className="adm-card">
        <header className="adm-card__head">
          <div>
            <span>
              Cadastradas
            </span>

            <h2>
              Campanhas
            </h2>
          </div>
        </header>

        <div className="adm-list">
          {campanhas.map(
            (item) => (
              <article
                className="adm-list-item"
                key={
                  item._id
                }
              >
                <div>
                  <div className="adm-list-item__title">
                    <strong>
                      {
                        item.codigo
                      }
                    </strong>

                    <StatusPill
                      tone={
                        item.ativo
                          ? "green"
                          : "red"
                      }
                    >
                      {item.ativo
                        ? "Ativo"
                        : "Inativo"}
                    </StatusPill>
                  </div>

                  <span>
                    {item.nome}
                  </span>

                  <small>
                    {item.descontoPercentual}% por{" "}
                    {item.duracaoMeses} mês(es) •{" "}
                    {item.usosConfirmados || 0}/
                    {item.limiteUsos} usos
                  </small>

                  {item.parceiroId ? (
                    <small>
                      Parceiro:{" "}
                      {
                        item
                          .parceiroId
                          .nome
                      }
                    </small>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    toggle(
                      item
                    )
                  }
                >
                  {item.ativo
                    ? "Desativar"
                    : "Ativar"}
                </button>
              </article>
            )
          )}
        </div>
      </section>
    </div>
  );
}

function PartnersTab({
  parceiros,
  onRefresh,
}) {
  const [
    form,
    setForm,
  ] = useState({
    nome: "",
    email: "",
    instagram: "",
    codigoPrincipal: "",
    tipo: "INFLUENCIADOR",
    comissaoTipo: "FIXA",
    comissaoValor: 30,
  });

  const [
    saving,
    setSaving,
  ] = useState(false);

  const submit =
    async (event) => {
      event.preventDefault();
      setSaving(true);

      try {
        await adminRequest(
          "/psy/admin/parceiros",
          {
            method:
              "POST",
            body:
              JSON.stringify({
                nome:
                  form.nome,
                email:
                  form.email,
                instagram:
                  form.instagram,
                codigoPrincipal:
                  form.codigoPrincipal,
                tipo:
                  form.tipo,
                comissao: {
                  tipo:
                    form.comissaoTipo,
                  valor:
                    Number(
                      form.comissaoValor
                    ),
                  quantidadePagamentos:
                    1,
                },
              }),
          }
        );

        setForm({
          nome: "",
          email: "",
          instagram: "",
          codigoPrincipal: "",
          tipo: "INFLUENCIADOR",
          comissaoTipo: "FIXA",
          comissaoValor: 30,
        });

        await onRefresh();
      } finally {
        setSaving(false);
      }
    };

  return (
    <div className="adm-two-column">
      <section className="adm-card">
        <header className="adm-card__head">
          <div>
            <span>
              Cadastro
            </span>

            <h2>
              Novo parceiro
            </h2>
          </div>
        </header>

        <form
          className="adm-form"
          onSubmit={submit}
        >
          <div className="adm-form__grid">
            {[
              [
                "nome",
                "Nome",
              ],
              [
                "email",
                "E-mail",
              ],
              [
                "instagram",
                "Instagram",
              ],
              [
                "codigoPrincipal",
                "Código principal",
              ],
            ].map(
              ([
                field,
                label,
              ]) => (
                <label
                  key={
                    field
                  }
                >
                  <span>
                    {label}
                  </span>

                  <input
                    value={
                      form[
                        field
                      ]
                    }
                    onChange={(
                      event
                    ) =>
                      setForm(
                        (
                          current
                        ) => ({
                          ...current,
                          [field]:
                            event
                              .target
                              .value,
                        })
                      )
                    }
                    required={
                      field ===
                      "nome"
                    }
                  />
                </label>
              )
            )}

            <label>
              <span>
                Comissão
              </span>

              <select
                value={
                  form.comissaoTipo
                }
                onChange={(
                  event
                ) =>
                  setForm(
                    (
                      current
                    ) => ({
                      ...current,
                      comissaoTipo:
                        event
                          .target
                          .value,
                    })
                  )
                }
              >
                <option value="FIXA">
                  Fixa (R$)
                </option>
                <option value="PERCENTUAL">
                  Percentual
                </option>
              </select>
            </label>

            <label>
              <span>
                Valor
              </span>

              <input
                type="number"
                min="0"
                step="0.01"
                value={
                  form.comissaoValor
                }
                onChange={(
                  event
                ) =>
                  setForm(
                    (
                      current
                    ) => ({
                      ...current,
                      comissaoValor:
                        event
                          .target
                          .value,
                    })
                  )
                }
              />
            </label>
          </div>

          <button
            className="adm-primary"
            type="submit"
            disabled={saving}
          >
            {saving
              ? "Salvando..."
              : "Cadastrar parceiro"}
          </button>
        </form>
      </section>

      <section className="adm-card">
        <header className="adm-card__head">
          <div>
            <span>
              Rede
            </span>

            <h2>
              Parceiros
            </h2>
          </div>
        </header>

        <div className="adm-list">
          {parceiros.map(
            (item) => (
              <article
                className="adm-list-item"
                key={
                  item._id
                }
              >
                <div>
                  <div className="adm-list-item__title">
                    <strong>
                      {
                        item.nome
                      }
                    </strong>

                    <StatusPill
                      tone={
                        item.ativo
                          ? "green"
                          : "red"
                      }
                    >
                      {item.ativo
                        ? "Ativo"
                        : "Inativo"}
                    </StatusPill>
                  </div>

                  <span>
                    {item.instagram ||
                      item.email ||
                      "Sem contato"}
                  </span>

                  <small>
                    Código:{" "}
                    {item.codigoPrincipal ||
                      "—"}{" "}
                    • Comissão:{" "}
                    {item.comissao
                      ?.tipo ===
                    "PERCENTUAL"
                      ? `${item.comissao?.valor || 0}%`
                      : formatBRL(
                          item
                            .comissao
                            ?.valor ||
                            0
                        )}
                  </small>
                </div>
              </article>
            )
          )}
        </div>
      </section>
    </div>
  );
}

function ConversionsTab({
  conversoes,
}) {
  return (
    <section className="adm-card">
      <header className="adm-card__head">
        <div>
          <span>
            Atribuição
          </span>

          <h2>
            Conversões por cupom
          </h2>
        </div>
      </header>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Parceiro</th>
              <th>Cliente</th>
              <th>Plano</th>
              <th>Status</th>
              <th>1º pagamento</th>
              <th>Valor</th>
            </tr>
          </thead>

          <tbody>
            {conversoes.map(
              (item) => (
                <tr
                  key={
                    item._id
                  }
                >
                  <td>
                    <strong>
                      {
                        item.codigo
                      }
                    </strong>
                  </td>

                  <td>
                    {item
                      .parceiroId
                      ?.nome ||
                      item
                        .campanhaId
                        ?.parceiroId
                        ?.nome ||
                      "—"}
                  </td>

                  <td>
                    {item
                      .usuarioId
                      ?.nomeUsuario ||
                      item.email ||
                      "—"}
                  </td>

                  <td>
                    {item.planId ||
                      "—"}
                  </td>

                  <td>
                    <StatusPill
                      tone={getStatusTone(
                        item.conversaoStatus
                      )}
                    >
                      {item.conversaoStatus}
                    </StatusPill>
                  </td>

                  <td>
                    {formatDate(
                      item.primeiroPagamentoEm
                    )}
                  </td>

                  <td>
                    {item.primeiroPagamentoValorCentavos
                      ? formatBRLFromCents(
                          item.primeiroPagamentoValorCentavos
                        )
                      : "—"}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CommissionsTab({
  comissoes,
  onRefresh,
}) {
  const markPaid =
    async (item) => {
      const ok =
        window.confirm(
          `Marcar a comissão de ${formatBRLFromCents(
            item.valorComissaoCentavos
          )} como paga?`
        );

      if (!ok) {
        return;
      }

      await adminRequest(
        `/psy/admin/comissoes/${item._id}/pagar`,
        {
          method:
            "PATCH",
          body:
            JSON.stringify({
              observacaoPagamento:
                "Marcada como paga pelo painel administrativo.",
            }),
        }
      );

      await onRefresh();
    };

  return (
    <section className="adm-card">
      <header className="adm-card__head">
        <div>
          <span>
            Financeiro de parceiros
          </span>

          <h2>
            Comissões
          </h2>
        </div>
      </header>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Parceiro</th>
              <th>Código</th>
              <th>Cliente</th>
              <th>Pagamento</th>
              <th>Comissão</th>
              <th>Status</th>
              <th>Gerada em</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {comissoes.map(
              (item) => (
                <tr
                  key={
                    item._id
                  }
                >
                  <td>
                    {
                      item
                        .parceiroId
                        ?.nome ||
                      "—"
                    }
                  </td>

                  <td>
                    {
                      item.codigo
                    }
                  </td>

                  <td>
                    {item
                      .usuarioId
                      ?.nomeUsuario ||
                      item
                        .usuarioId
                        ?.email ||
                      "—"}
                  </td>

                  <td>
                    {formatBRLFromCents(
                      item.valorPagamentoCentavos
                    )}
                  </td>

                  <td>
                    <strong>
                      {formatBRLFromCents(
                        item.valorComissaoCentavos
                      )}
                    </strong>
                  </td>

                  <td>
                    <StatusPill
                      tone={getStatusTone(
                        item.status
                      )}
                    >
                      {item.status}
                    </StatusPill>
                  </td>

                  <td>
                    {formatDate(
                      item.geradaEm
                    )}
                  </td>

                  <td>
                    {item.status !==
                    "PAGA" ? (
                      <button
                        className="adm-link-button"
                        type="button"
                        onClick={() =>
                          markPaid(
                            item
                          )
                        }
                      >
                        Marcar paga
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const FOLLOWUP_STATUS_OPTIONS = [
  ["NOVO", "Novo"],
  ["CONTATADO", "Contatado"],
  ["AGUARDANDO_RETORNO", "Aguardando retorno"],
  ["INTERESSADO", "Interessado"],
  ["SEM_INTERESSE", "Sem interesse"],
  ["NAO_RESPONDEU", "Não respondeu"],
  ["CONVERTIDO", "Convertido"],
];

const FOLLOWUP_REASON_OPTIONS = [
  ["", "Não informado"],
  ["PRECO", "Preço"],
  ["CARTAO_TESTE", "Cartão no teste"],
  ["DUVIDA_PRODUTO", "Dúvida sobre o produto"],
  ["FALTOU_FUNCIONALIDADE", "Faltou funcionalidade"],
  ["PROBLEMA_TECNICO", "Problema técnico"],
  ["AINDA_NAO_ATENDE", "Ainda não começou a atender"],
  ["ESCOLHEU_CONCORRENTE", "Escolheu outro sistema"],
  ["NAO_RESPONDEU", "Não respondeu"],
  ["OUTRO", "Outro"],
];

function followupStatusLabel(value) {
  return (
    FOLLOWUP_STATUS_OPTIONS.find(([key]) => key === value)?.[1] ||
    value ||
    "Novo"
  );
}


function followupStatusTone(value) {
  const status = String(value || "").toUpperCase();

  if (["CONVERTIDO", "INTERESSADO"].includes(status)) return "green";
  if (["SEM_INTERESSE", "NAO_RESPONDEU"].includes(status)) return "red";
  if (["CONTATADO", "AGUARDANDO_RETORNO"].includes(status)) return "amber";
  return "slate";
}

function temperatureTone(value) {
  const temperature = String(value || "").toUpperCase();
  if (temperature === "FOLLOWUP") return "amber";
  if (temperature === "ESFRIANDO") return "red";
  if (temperature === "CONVERTIDO") return "green";
  return "slate";
}

function formatElapsedMinutes(minutes) {
  const total = Math.max(0, Number(minutes || 0));
  if (total < 60) return `${total} min`;
  if (total < 1440) return `${Math.floor(total / 60)} h`;
  return `${Math.floor(total / 1440)} d`;
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function normalizeWhatsAppNumber(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55") && digits.length >= 12) return digits;
  if ([10, 11].includes(digits.length)) return `55${digits}`;
  return digits;
}

function FollowupsTab({
  followups,
  summary,
  onRefresh,
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [temperatureFilter, setTemperatureFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    status: "NOVO",
    proximoContatoEm: "",
    motivoPerda: "",
    observacoes: "",
    observacaoContato: "",
  });

  const filtered = useMemo(() => {
    const query = String(search || "").trim().toLowerCase();

    return (followups || []).filter((item) => {
      if (statusFilter && item.status !== statusFilter) return false;
      if (temperatureFilter && item.temperatura !== temperatureFilter) return false;

      if (!query) return true;

      return [
        item.nome,
        item.email,
        item.telefone,
        item.crp,
        item.plano,
        item.etapaLabel,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [followups, search, statusFilter, temperatureFilter]);

  const openItem = (item) => {
    setError("");
    setSelected(item);
    setForm({
      status: item.status || "NOVO",
      proximoContatoEm: toDateTimeLocal(item.proximoContatoEm),
      motivoPerda: item.motivoPerda || "",
      observacoes: item.observacoes || "",
      observacaoContato: "",
    });
  };

  const submit = async ({ registrarContato = false } = {}) => {
    if (!selected?.id || saving) return;

    setSaving(true);
    setError("");

    try {
      const result = await adminRequest(`/psy/admin/followups/${selected.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          status: form.status,
          proximoContatoEm: form.proximoContatoEm
            ? new Date(form.proximoContatoEm).toISOString()
            : null,
          motivoPerda: form.motivoPerda || null,
          observacoes: form.observacoes,
          registrarContato,
          observacaoContato: form.observacaoContato,
          historicoDescricao: form.observacaoContato,
        }),
      });

      setSelected(result.data || null);
      setForm((current) => ({ ...current, observacaoContato: "" }));
      await onRefresh();
    } catch (requestError) {
      setError(requestError.message || "Não foi possível salvar o follow-up.");
    } finally {
      setSaving(false);
    }
  };

  const kpis = [
    ["Cadastros interrompidos", summary?.abandonados || 0, "30 min ou mais sem finalização"],
    ["Novos", summary?.novos || 0, "ainda sem acompanhamento"],
    ["Follow-up recomendado", summary?.followupRecomendado || 0, "prioridade comercial atual"],
    ["Recuperados", summary?.recuperados || 0, "converteram após acompanhamento"],
    ["Taxa de recuperação", `${Number(summary?.taxaRecuperacao || 0).toFixed(1)}%`, "sobre contatos realizados"],
  ];

  return (
    <div className="adm-followup">
      <section className="adm-followup-kpis">
        {kpis.map(([label, value, hint]) => (
          <article className="adm-kpi" key={label}>
            <div className="adm-kpi__icon"><MessageCircle size={18} /></div>
            <span>{label}</span>
            <strong>{value}</strong>
            <small>{hint}</small>
          </article>
        ))}
      </section>

      <section className="adm-card">
        <header className="adm-card__head adm-card__head--followup">
          <div>
            <span>Conversão</span>
            <h2>Cadastros que não foram finalizados</h2>
          </div>
          <small>
            Registros entram nesta fila após 30 minutos sem atividade.
          </small>
        </header>

        <div className="adm-followup-filters">
          <label className="adm-followup-search">
            <Search size={16} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome, e-mail, telefone ou CRP"
            />
          </label>

          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">Todos os status</option>
            {FOLLOWUP_STATUS_OPTIONS.filter(([key]) => key !== "CONVERTIDO").map(([key, label]) => (
              <option value={key} key={key}>{label}</option>
            ))}
          </select>

          <select value={temperatureFilter} onChange={(event) => setTemperatureFilter(event.target.value)}>
            <option value="">Todas as prioridades</option>
            <option value="INTERROMPIDO">Interrompido hoje</option>
            <option value="FOLLOWUP">Follow-up recomendado</option>
            <option value="ESFRIANDO">Lead esfriando</option>
          </select>
        </div>

        <div className="adm-table-wrap">
          <table className="adm-table adm-followup-table">
            <thead>
              <tr>
                <th>Pessoa</th>
                <th>Contato</th>
                <th>Plano</th>
                <th>Etapa</th>
                <th>Sem atividade</th>
                <th>Prioridade</th>
                <th>Status</th>
                <th>Próximo contato</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.nome || "Nome não informado"}</strong>
                    <small className="adm-table-sub">{item.crp || ""}</small>
                  </td>
                  <td>
                    {item.email || item.telefone || "—"}
                    {item.email && item.telefone ? (
                      <small className="adm-table-sub">{item.telefone}</small>
                    ) : null}
                  </td>
                  <td>{item.plano || "—"}</td>
                  <td>{item.etapaLabel || "—"}</td>
                  <td>{formatElapsedMinutes(item.minutosSemAtividade)}</td>
                  <td>
                    <StatusPill tone={temperatureTone(item.temperatura)}>
                      {item.temperaturaLabel}
                    </StatusPill>
                  </td>
                  <td>
                    <StatusPill tone={followupStatusTone(item.status)}>
                      {followupStatusLabel(item.status)}
                    </StatusPill>
                  </td>
                  <td>{item.proximoContatoEm ? formatDate(item.proximoContatoEm) : "—"}</td>
                  <td>
                    <button className="adm-link-button" type="button" onClick={() => openItem(item)}>
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!filtered.length ? (
            <div className="adm-followup-empty">
              Nenhum cadastro encontrado com os filtros atuais.
            </div>
          ) : null}
        </div>
      </section>

      {selected ? (
        <div className="adm-followup-modal-backdrop" role="presentation">
          <section className="adm-followup-modal" role="dialog" aria-modal="true">
            <header className="adm-followup-modal__head">
              <div>
                <span>Follow-up</span>
                <h2>{selected.nome || selected.email || "Cadastro sem nome"}</h2>
                <small>{selected.plano} · {selected.etapaLabel}</small>
              </div>
              <button type="button" onClick={() => setSelected(null)} aria-label="Fechar">
                <X size={18} />
              </button>
            </header>

            <div className="adm-followup-contact-actions">
              {selected.email ? (
                <a href={`mailto:${selected.email}`}><Mail size={15} /> E-mail</a>
              ) : null}
              {selected.telefone ? (
                <a
                  href={`https://wa.me/${normalizeWhatsAppNumber(selected.telefone)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MessageCircle size={15} /> WhatsApp
                </a>
              ) : null}
            </div>

            <div className="adm-followup-modal__summary">
              <div><span>E-mail</span><strong>{selected.email || "—"}</strong></div>
              <div><span>Telefone</span><strong>{selected.telefone || "—"}</strong></div>
              <div><span>Última atividade</span><strong>{formatDate(selected.atualizadoEm)}</strong></div>
              <div><span>Tempo parado</span><strong>{formatElapsedMinutes(selected.minutosSemAtividade)}</strong></div>
            </div>

            {selected.ultimoErroFinalizacao ? (
              <div className="adm-alert adm-alert--error">
                Último erro de finalização: {selected.ultimoErroFinalizacao}
              </div>
            ) : null}

            <div className="adm-followup-form">
              <label>
                <span>Status</span>
                <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
                  {FOLLOWUP_STATUS_OPTIONS.filter(([key]) => key !== "CONVERTIDO").map(([key, label]) => (
                    <option value={key} key={key}>{label}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Próximo contato</span>
                <input
                  type="datetime-local"
                  value={form.proximoContatoEm}
                  onChange={(event) => setForm((current) => ({ ...current, proximoContatoEm: event.target.value }))}
                />
              </label>

              <label>
                <span>Motivo de perda</span>
                <select value={form.motivoPerda} onChange={(event) => setForm((current) => ({ ...current, motivoPerda: event.target.value }))}>
                  {FOLLOWUP_REASON_OPTIONS.map(([key, label]) => (
                    <option value={key} key={key || "none"}>{label}</option>
                  ))}
                </select>
              </label>

              <label className="adm-followup-form__full">
                <span>Observações gerais</span>
                <textarea
                  rows={3}
                  value={form.observacoes}
                  onChange={(event) => setForm((current) => ({ ...current, observacoes: event.target.value }))}
                  placeholder="Contexto comercial útil para os próximos contatos."
                />
              </label>

              <label className="adm-followup-form__full">
                <span>Registro do contato / observação de histórico</span>
                <textarea
                  rows={3}
                  value={form.observacaoContato}
                  onChange={(event) => setForm((current) => ({ ...current, observacaoContato: event.target.value }))}
                  placeholder="Ex.: contato realizado por WhatsApp; informou dúvida sobre o cartão no período de teste."
                />
              </label>
            </div>

            {error ? <div className="adm-alert adm-alert--error">{error}</div> : null}

            <div className="adm-followup-modal__actions">
              <button type="button" className="adm-followup-secondary" disabled={saving} onClick={() => submit({ registrarContato: false })}>
                Salvar
              </button>
              <button type="button" className="adm-primary" disabled={saving} onClick={() => submit({ registrarContato: true })}>
                {saving ? "Salvando..." : "Registrar contato"}
              </button>
            </div>

            <div className="adm-followup-history">
              <div className="adm-followup-history__head">
                <Clock3 size={16} />
                <strong>Histórico</strong>
              </div>

              {selected.historico?.length ? (
                selected.historico.map((item) => (
                  <article key={item.id || `${item.tipo}-${item.criadoEm}`}>
                    <span>{item.tipo.replaceAll("_", " ")}</span>
                    <strong>{item.descricao || "Registro atualizado."}</strong>
                    <small>{item.usuarioNome} · {formatDate(item.criadoEm)}</small>
                  </article>
                ))
              ) : (
                <p>Nenhum contato registrado até o momento.</p>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function UsersTab({
  usuarios,
}) {
  return (
    <section className="adm-card">
      <header className="adm-card__head">
        <div>
          <span>
            Base PsyAssist
          </span>

          <h2>
            Usuários
          </h2>
        </div>
      </header>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Plano</th>
              <th>Assinatura</th>
              <th>Trial</th>
              <th>Permissão</th>
              <th>Cadastro</th>
            </tr>
          </thead>

          <tbody>
            {usuarios.map(
              (item) => (
                <tr
                  key={
                    item._id
                  }
                >
                  <td>
                    <strong>
                      {item.nomeUsuario ||
                        "—"}
                    </strong>
                  </td>

                  <td>
                    {item.email}
                  </td>

                  <td>
                    {item.plano ||
                      "—"}
                  </td>

                  <td>
                    <StatusPill
                      tone={getStatusTone(
                        item.statusAssinatura
                      )}
                    >
                      {item.statusAssinatura ||
                        "—"}
                    </StatusPill>
                  </td>

                  <td>
                    {item.isTrial
                      ? "Sim"
                      : "Não"}
                  </td>

                  <td>
                    {item.permissao ||
                      "usuario"}
                  </td>

                  <td>
                    {formatDate(
                      item.createdAt ||
                      item.dataCadastro
                    )}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AdminPanel({
  initialUser,
  onLogout,
}) {
  const [
    tab,
    setTab,
  ] = useState(
    "dashboard"
  );

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    dashboard,
    setDashboard,
  ] = useState(null);

  const [
    campanhas,
    setCampanhas,
  ] = useState([]);

  const [
    parceiros,
    setParceiros,
  ] = useState([]);

  const [
    conversoes,
    setConversoes,
  ] = useState([]);

  const [
    comissoes,
    setComissoes,
  ] = useState([]);

  const [
    followups,
    setFollowups,
  ] = useState([]);

  const [
    followupSummary,
    setFollowupSummary,
  ] = useState(null);

  const [
    usuarios,
    setUsuarios,
  ] = useState([]);

  const loadAll =
    useCallback(
      async () => {
        setLoading(true);
        setError("");

        try {
          const [
            dashboardResult,
            campanhasResult,
            parceirosResult,
            conversoesResult,
            comissoesResult,
            usuariosResult,
          ] =
            await Promise.all([
              adminRequest(
                "/psy/admin/dashboard"
              ),
              adminRequest(
                "/psy/admin/campanhas"
              ),
              adminRequest(
                "/psy/admin/parceiros"
              ),
              adminRequest(
                "/psy/admin/conversoes"
              ),
              adminRequest(
                "/psy/admin/comissoes"
              ),
              adminRequest(
                "/psy/admin/usuarios"
              ),
            ]);

          setDashboard(
            dashboardResult
              .data
          );

          setCampanhas(
            campanhasResult
              .data ||
            []
          );

          setParceiros(
            parceirosResult
              .data ||
            []
          );

          setConversoes(
            conversoesResult
              .data ||
            []
          );

          setComissoes(
            comissoesResult
              .data ||
            []
          );

          setUsuarios(
            usuariosResult
              .data ||
            []
          );

          try {
            const [
              followupsResult,
              followupSummaryResult,
            ] = await Promise.all([
              adminRequest(
                "/psy/admin/followups"
              ),
              adminRequest(
                "/psy/admin/followups/resumo"
              ),
            ]);

            setFollowups(
              followupsResult
                .data ||
              []
            );

            setFollowupSummary(
              followupSummaryResult
                .data ||
              null
            );
          } catch (followupError) {
            console.error(
              "Não foi possível carregar o follow-up administrativo:",
              followupError
            );
            setFollowups([]);
            setFollowupSummary(null);
          }
        } catch (
          requestError
        ) {
          setError(
            requestError
              .message ||
            "Não foi possível carregar o painel."
          );

          if (
            [
              401,
              403,
            ].includes(
              requestError.status
            )
          ) {
            onLogout();
          }
        } finally {
          setLoading(false);
        }
      },
      [
        onLogout,
      ]
    );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const currentTab =
    useMemo(
      () =>
        TABS.find(
          (item) =>
            item.id ===
            tab
        ) ||
        TABS[0],
      [tab]
    );

  return (
    <main className="adm-page">
      <aside className="adm-sidebar">
        <div className="adm-brand">
          <span className="adm-brand__mark">
            PS
          </span>

          <span>
            <strong>
              PsyAssist Pro
            </strong>

            <small>
              Administração
            </small>
          </span>
        </div>

        <nav>
          {TABS.map(
            (item) => {
              const Icon =
                item.icon;

              return (
                <button
                  type="button"
                  key={
                    item.id
                  }
                  className={
                    tab ===
                    item.id
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setTab(
                      item.id
                    )
                  }
                >
                  <Icon size={18} />
                  <span className="adm-nav-label">{item.label}</span>
                  {item.id === "followups" && Number(followupSummary?.novos || 0) > 0 ? (
                    <span className="adm-nav-count">{followupSummary.novos}</span>
                  ) : null}
                </button>
              );
            }
          )}
        </nav>

        <div className="adm-sidebar__user">
          <span>
            Conectado como
          </span>

          <strong>
            {initialUser
              ?.nome ||
              "Administrador"}
          </strong>

          <small>
            {initialUser
              ?.email ||
              ""}
          </small>

          <button
            type="button"
            onClick={
              onLogout
            }
          >
            <LogOut size={16} />
            Sair
          </button>
        </div>
      </aside>

      <section className="adm-content">
        <header className="adm-topbar">
          <div>
            <span>
              Backoffice
            </span>

            <h1>
              {
                currentTab.label
              }
            </h1>
          </div>

          <button
            className="adm-refresh"
            type="button"
            onClick={
              loadAll
            }
            disabled={
              loading
            }
          >
            <RefreshCw
              size={17}
              className={
                loading
                  ? "spin"
                  : ""
              }
            />

            Atualizar
          </button>
        </header>

        {error ? (
          <div className="adm-alert adm-alert--error">
            {error}
          </div>
        ) : null}

        {loading &&
        !dashboard ? (
          <div className="adm-loading">
            Carregando painel...
          </div>
        ) : null}

        {tab ===
        "dashboard" ? (
          <DashboardTab
            data={
              dashboard
            }
          />
        ) : null}

        {tab ===
        "campanhas" ? (
          <CampaignsTab
            campanhas={
              campanhas
            }
            parceiros={
              parceiros
            }
            onRefresh={
              loadAll
            }
          />
        ) : null}

        {tab ===
        "parceiros" ? (
          <PartnersTab
            parceiros={
              parceiros
            }
            onRefresh={
              loadAll
            }
          />
        ) : null}

        {tab ===
        "conversoes" ? (
          <ConversionsTab
            conversoes={
              conversoes
            }
          />
        ) : null}

        {tab ===
        "comissoes" ? (
          <CommissionsTab
            comissoes={
              comissoes
            }
            onRefresh={
              loadAll
            }
          />
        ) : null}

        {tab ===
        "followups" ? (
          <FollowupsTab
            followups={followups}
            summary={followupSummary}
            onRefresh={loadAll}
          />
        ) : null}

        {tab ===
        "usuarios" ? (
          <UsersTab
            usuarios={
              usuarios
            }
          />
        ) : null}
      </section>
    </main>
  );
}

export default function AdminApp() {
  const [
    user,
    setUser,
  ] = useState(
    getStoredAdminUser()
  );

  const [
    checking,
    setChecking,
  ] = useState(
    Boolean(
      getAdminToken()
    )
  );

  const logout =
    useCallback(
      () => {
        clearAdminSession();
        setUser(null);
      },
      []
    );

  useEffect(() => {
    let mounted =
      true;

    async function verify() {
      const token =
        getAdminToken();

      if (!token) {
        if (mounted) {
          setChecking(
            false
          );
        }

        return;
      }

      try {
        const result =
          await adminRequest(
            "/psy/admin/me"
          );

        if (mounted) {
          setUser(
            result.user
          );
        }
      } catch {
        if (mounted) {
          logout();
        }
      } finally {
        if (mounted) {
          setChecking(
            false
          );
        }
      }
    }

    verify();

    return () => {
      mounted =
        false;
    };
  }, [logout]);

  if (checking) {
    return (
      <main className="adm-login">
        <div className="adm-loading">
          Validando acesso...
        </div>
      </main>
    );
  }

  if (!user) {
    return (
      <AdminLogin
        onSuccess={
          setUser
        }
      />
    );
  }

  return (
    <AdminPanel
      initialUser={
        user
      }
      onLogout={
        logout
      }
    />
  );
}
